import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { siteInfo } from '@/mocks/site';
import { hotList } from '@/mocks/hotList';
import TrendBadge from '@/components/feature/TrendBadge';

export default function HomeHero() {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const board = hotList.slice(0, 5);
  const maxHeat = board[0]?.heat ?? 1;

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/ranking?q=${encodeURIComponent(value)}` : '/ranking');
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-16 h-80 w-80 rounded-full bg-accent-100/70 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-secondary-100/60 blur-3xl" />

      <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-12 pt-10 md:px-6 md:pb-16 md:pt-16">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <span className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3.5 py-1.5 text-xs font-medium text-primary-700">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping-dot rounded-full bg-primary-500" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
              </span>
              今日热议焦点已更新
            </span>

            <h1 className="mt-5 animate-fade-up font-heading text-4xl leading-[1.15] tracking-wide text-foreground-950 md:text-6xl">
              瓜圈速报
              <span className="mt-2 block text-2xl text-foreground-800 md:text-4xl">
                今天的瓜，按<span className="text-primary-600">热度</span>排好了
              </span>
            </h1>

            <p className="mt-5 max-w-xl animate-fade-up text-sm leading-relaxed text-foreground-600 delay-1 md:text-base">
              瓜圈速报热度排行实时更新，汇聚全网热门榜单与话题榜。不用翻十个 App，打开这一页，就知道今天哪口瓜最值得围观。
            </p>

            <form onSubmit={handleSearch} className="mt-7 flex max-w-xl animate-fade-up flex-col gap-3 sm:flex-row delay-2" role="search">
              <div className="relative flex-1">
                <i className="ri-search-2-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜一搜今天在传什么瓜"
                  aria-label="搜索热点"
                  className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="h-12 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                搜索热度榜
              </button>
            </form>

            <div className="mt-6 flex animate-fade-up flex-wrap items-center gap-2 delay-3">
              <span className="text-xs text-foreground-500">热搜词：</span>
              {['恋情观察', '古装剧口碑', '音乐节秒空', '综艺名场面'].map((tag) => (
                <Link
                  key={tag}
                  to={`/ranking?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <dl className="mt-9 grid animate-fade-up grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-7 delay-4 md:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label}>
                  <dt className="text-xs text-foreground-500">{stat.label}</dt>
                  <dd className="mt-1.5 font-heading text-2xl tabular-nums text-foreground-950 md:text-3xl">
                    {stat.value}
                    <span className="ml-1 text-sm text-foreground-500">{stat.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="animate-fade-up lg:col-span-5 delay-2">
            <div className="rounded-card border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                  <i className="ri-fire-line text-lg text-primary-500" />
                  实时热度榜
                </h2>
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping-dot rounded-full bg-secondary-500" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-secondary-500" />
                  </span>
                  10 分钟前刷新
                </span>
              </div>

              <ol className="mt-5 space-y-4">
                {board.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/article/${item.id}`} className="group block">
                      <div className="flex items-start gap-3">
                        <span
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            index === 0
                              ? 'bg-primary-500 text-background-50'
                              : index === 1
                                ? 'bg-accent-500 text-primary-950'
                                : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </p>
                          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-100">
                            <div
                              className={`h-full rounded-full ${index === 0 ? 'bg-primary-500' : 'bg-accent-400'}`}
                              style={{ width: `${Math.max((item.heat / maxHeat) * 100, 18)}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="font-heading text-base tabular-nums text-foreground-900">{item.heat}</span>
                          <TrendBadge trend={item.trend} value={item.trendValue} compact />
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>

              <Link
                to="/ranking"
                className="mt-5 flex h-11 items-center justify-center gap-2 rounded-md bg-foreground-950 text-sm font-medium text-background-50 transition hover:bg-primary-600"
              >
                查看完整 100 条榜单
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}